import React from "react";
import { Menu, MenuButton, MenuList, MenuItem, Button, Box, HStack, Icon } from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { DataTable, Card, CardActions, CardContent, CardHeader, CardHeading } from '@ucc/react/ui';
import { useAgreements } from '~/pages/master-data/ui/GeneralAgreements/hooks/useAgreements';
import { HasPermission } from '~/layout/HasPermission';
import { Rahmenvertrag } from "~/gql/ucpw/graphql";

type Props = {
    activeGeneralAgreement?: Rahmenvertrag;
    setActiveGeneralAgreement?: (arg: Rahmenvertrag) => void
}

export function Agreements({ activeGeneralAgreement, setActiveGeneralAgreement }: any) {
    const { onCreate, onImport, standard, ...tableProps } = useAgreements();
    const data: any[] = tableProps.data || [];

    // Keep the active agreement valid: pick the first one initially and jump
    // back to it whenever the active one is no longer in the list (e.g. after
    // a delete). Gated on !loading so it never fires during an import refetch.
    React.useEffect(() => {
        if (tableProps.loading || !data.length) return;
        const exists = activeGeneralAgreement && data.some((d) => d?.id === activeGeneralAgreement?.id);
        if (!exists) setActiveGeneralAgreement(data[0]);
    }, [tableProps.loading, data, activeGeneralAgreement, setActiveGeneralAgreement]);

    // Highlight the row of the active agreement (DataTable seeds activeRow on
    // mount, so remount via key when the active agreement changes).
    const activeIndex = Math.max(0, data.findIndex((d) => d?.id === activeGeneralAgreement?.id));

    return (
        <Card boxShadow="none">
            <CardHeader>
                <CardHeading>Rahmenvereinbarungen</CardHeading>
                <HasPermission resource="masterData.agreements" permission="create">
                    {/* minW=0 lets this flex item shrink below the button's
                        content width, so the label can truncate instead of
                        overflowing into the neighbouring card. */}
                    <CardActions minW={0}>
                        <Menu>
                            {({ isOpen }) => (
                                <>
                                    <MenuButton
                                        as={Button}
                                        isActive={isOpen}
                                        colorScheme="blue"
                                        // Allow the button to shrink below its
                                        // content so the label can truncate
                                        // instead of overflowing into the table.
                                        minW={0}
                                        aria-label="Neue Rahmenvereinbarung"
                                        data-test-id="agreements-add-menu"
                                    >
                                        {/* Explicit flex row so only the label
                                            shrinks: the chevron keeps its size,
                                            the text ellipsises. */}
                                        <HStack spacing={2} minW={0} w="full">
                                            <Box
                                                minW={0}
                                                overflow="hidden"
                                                textOverflow="ellipsis"
                                                whiteSpace="nowrap"
                                            >
                                                Neue Rahmenvereinbarung
                                            </Box>
                                            <Icon
                                                as={isOpen ? FiChevronUp : FiChevronDown}
                                                flexShrink={0}
                                            />
                                        </HStack>
                                    </MenuButton>
                                    <MenuList>
                                        <MenuItem onClick={onCreate} data-test-id="agreements-add-new">
                                            Neue Vereinbarung
                                        </MenuItem>
                                        <MenuItem onClick={onImport} data-test-id="agreements-add-csv">
                                            Aus CSV importieren
                                        </MenuItem>
                                    </MenuList>
                                </>
                            )}
                        </Menu>
                    </CardActions>
                </HasPermission>
            </CardHeader>
            <CardContent>
                <DataTable
                    key={`agreements-active-${activeIndex}`}
                    activeRow={String(activeIndex)}
                    onClick={({ original }) => setActiveGeneralAgreement(original)}
                    {...tableProps}
                />
            </CardContent>
        </Card>
    )
}